import React from 'react';
import { AlertTriangle, CheckCircle, CheckCircle2 } from 'lucide-react';
import { ChildrenProps, classNames } from '@cotera/client/app/components/utils';

const FILLED_VARIANT_STYLE_MAP = {
  neutral: 'bg-muted text-muted-foreground border-transparent',
  success: 'bg-success/10 text-success border-success/20',
  warning: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  negative: 'bg-red-500/10 text-red-600 border-red-500/20',
  positive: 'bg-success/10 text-success border-success/20',
  secondary: 'bg-muted text-muted-foreground border-transparent',
  primary: 'bg-primary-background text-alt-text border-transparent',
  accent: 'bg-accent-background text-alt-text border-transparent',
} as const;

const INLINE_VARIANT_STYLE_MAP = {
  neutral: 'text-muted-foreground',
  success: 'text-green-600',
  warning: 'text-amber-600',
  negative: 'text-red-600',
  positive: 'text-green-600',
  secondary: 'text-muted-foreground',
  primary: 'text-primary',
  accent: 'text-foreground',
} as const;

const FILLED_VARIANT_ICONS: Partial<
  Record<keyof typeof FILLED_VARIANT_STYLE_MAP, typeof CheckCircle>
> = {
  success: CheckCircle,
  positive: CheckCircle,
  warning: AlertTriangle,
};

const INLINE_VARIANT_ICONS: Partial<
  Record<keyof typeof INLINE_VARIANT_STYLE_MAP, typeof CheckCircle2>
> = {
  success: CheckCircle2,
  positive: CheckCircle2,
  warning: AlertTriangle,
};

export type ChipVariant = keyof typeof FILLED_VARIANT_STYLE_MAP;
export type ChipAppearance = 'filled' | 'inline';

export const Chip: React.FC<
  ChildrenProps & {
    className?: string;
    variant?: ChipVariant;
    appearance?: ChipAppearance;
    /** @deprecated Use `variant` instead. */
    type?: ChipVariant;
    icon?: React.ReactNode;
    onClick?: () => void;
    tight?: boolean;
  }
> = ({
  children,
  className,
  variant,
  appearance = 'filled',
  type,
  icon,
  onClick,
  tight = false,
}) => {
  const resolvedVariant = variant ?? type ?? 'neutral';
  const isInline = appearance === 'inline';
  const DefaultIcon = isInline
    ? INLINE_VARIANT_ICONS[resolvedVariant]
    : FILLED_VARIANT_ICONS[resolvedVariant];
  const resolvedIcon =
    icon ??
    (DefaultIcon !== undefined ? (
      <DefaultIcon className="h-3.5 w-3.5" />
    ) : null);

  const Component = onClick !== undefined ? 'button' : 'span';

  return (
    <Component
      onClick={onClick}
      type={onClick !== undefined ? 'button' : undefined}
      className={classNames(
        'inline-flex items-center text-xs',
        isInline ? 'gap-1.5' : 'gap-1 rounded-full border font-medium',
        isInline
          ? INLINE_VARIANT_STYLE_MAP[resolvedVariant]
          : classNames(
              tight ? 'px-1.5 py-0.5' : 'px-2 py-0.5',
              FILLED_VARIANT_STYLE_MAP[resolvedVariant]
            ),
        className
      )}
    >
      {resolvedIcon}
      {children}
    </Component>
  );
};
